// src/components/AuthForm.jsx
import React, { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../config/firebase'; // <--- CORRECT import path
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from "sonner"; // Import toast from sonner

// Shadcn UI Imports
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Icons
import { Loader2, Mail, Lock, User, Chrome } from "lucide-react"; // Example icons

function AuthForm({ onSuccess }) {
  // Component state remains the same
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Clear messages/errors when switching modes
  useEffect(() => {
    setError(null);
    setMessage(null);
  }, [isLoginMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAuthError = (err) => {
    console.error('Authentication error:', err.code, err.message);
    let friendlyMessage = 'An error occurred. Please try again.';
    switch (err.code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        friendlyMessage = 'Invalid email or password.';
        break;
      case 'auth/email-already-in-use':
        friendlyMessage =
          'This email address is already registered. Try logging in or resetting your password.';
        setIsLoginMode(true);
        break;
      case 'auth/weak-password':
        friendlyMessage = 'Password should be at least 6 characters.';
        break;
      case 'auth/invalid-email':
        friendlyMessage = 'Please enter a valid email address.';
        break;
      case 'auth/cancelled-popup-request':
      case 'auth/popup-closed-by-user':
        friendlyMessage = null;
        break;
      case 'auth/popup-blocked':
        friendlyMessage = 'Popup blocked by browser. Please enable popups for this site.';
        break;
      default:
        friendlyMessage = `An unexpected error occurred (${err.code}).`;
    }
    if (friendlyMessage) {
      toast.error("Authentication Error", { description: friendlyMessage });
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isLoginMode) {
        // Login logic
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        toast.success("Login Successful", {
          description: "Welcome back!",
          duration: 3000
        });
        onSuccess?.();
      } else {
        // Registration logic
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        
        // Update profile with first and last name
        await updateProfile(userCredential.user, {
          displayName: `${formData.firstName} ${formData.lastName}`.trim()
        });

        // Create user profile in Firestore
        const baseUrl = import.meta.env.DEV
          ? 'http://localhost:5001/dagslys-dc425/europe-west1'
          : 'https://europe-west1-dagslys-dc425.cloudfunctions.net';

        // Get a fresh ID token
        const idToken = await userCredential.user.getIdToken(true);

        const response = await fetch(`${baseUrl}/createUserProfile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create user profile');
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Failed to create user profile');
        }

        toast.success("Registration Successful", {
          description: "Your account has been created. Please wait for admin approval.",
          duration: 3000
        });
        onSuccess?.();
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    setMessage(null);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);

      // Get the ID token for authentication
      const idToken = await result.user.getIdToken();

      const baseUrl = import.meta.env.DEV
        ? 'http://localhost:5001/dagslys-dc425/europe-west1'
        : 'https://europe-west1-dagslys-dc425.cloudfunctions.net';

      // Check if user profile exists
      const checkProfileResponse = await fetch(`${baseUrl}/checkUserProfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!checkProfileResponse.ok) {
        throw new Error('Failed to check user profile');
      }

      const { exists } = await checkProfileResponse.json();

      if (!exists) {
        // Create user profile in Firestore only if it doesn't exist
        const profileResponse = await fetch(`${baseUrl}/createUserProfile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            firstName: result.user.displayName?.split(' ')[0] || '',
            lastName: result.user.displayName?.split(' ').slice(1).join(' ') || ''
          })
        });

        if (!profileResponse.ok) {
          const errorData = await profileResponse.json();
          throw new Error(errorData.message || 'Failed to create user profile');
        }

        const profileResult = await profileResponse.json();
        if (!profileResult.success) {
          throw new Error(profileResult.message || 'Failed to create user profile');
        }

        // Send admin access request only for new users
        const adminResponse = await fetch(`${baseUrl}/requestAdminAccess`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            userId: result.user.uid,
            userEmail: result.user.email,
            userName: result.user.displayName,
          }),
        });

        if (!adminResponse.ok) {
          throw new Error('Failed to send admin notification');
        }

        toast.success("Account Created", {
          description: "Your account has been created. Please wait for admin approval.",
          duration: 3000
        });
      } else {
        toast.success("Login Successful", {
          description: "Welcome back!",
          duration: 3000
        });
      }

      onSuccess?.();
    } catch (error) {
      console.error('Google sign-in error:', error);
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      setError("Please enter your email address to reset password.");
      return;
    }
    setIsLoading(true);
    setError(null);
    setMessage(null);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      toast.success("Password Reset Email Sent", {
        description: "Please check your inbox (including spam folder) for reset instructions.",
        duration: 10000
      });
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
        setError("No account found with this email address.");
      } else {
        handleAuthError(err)
      }
    } finally {
      setIsLoading(false);
    }
  }

  // --- Render the Form using Shadcn UI ---
  return (
    <div className="w-full p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold">
          {isLoginMode ? 'Welcome Back!' : 'Request Access'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isLoginMode
            ? 'Enter your credentials to log in.'
            : 'Fill out the form to request access.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} id="email-password-form" className="space-y-4">
        {!isLoginMode && (
          <>
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                required={!isLoginMode}
                placeholder="Enter your first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                required={!isLoginMode}
                placeholder="Enter your last name"
              />
            </div>
          </>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            placeholder="Enter your email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            placeholder="Enter your password"
          />
        </div>
        {error && <p className="text-sm text-destructive text-center">{error}</p>}
        {message && <p className="text-sm text-primary text-center">{message}</p>}
      </form>

      <Button type="submit" form="email-password-form" disabled={isLoading} className="w-full">
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {isLoginMode ? 'Login with email' : 'Submit Access Request'}
      </Button>

      <div className="flex flex-col gap-4 mt-6">
        <Button
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full"
        >
          <Chrome className="mr-2 h-4 w-4" />
          Login with Google
        </Button>

        {isLoginMode && (
          <Button
            variant="link"
            onClick={handlePasswordReset}
            disabled={isLoading || !formData.email}
            className="p-0 h-auto text-sm text-foreground"
          >
            Forgot Password?
          </Button>
        )}

        <div className="relative w-full my-2">
          <Separator />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-background px-2 text-xs text-muted-foreground">
              OR
            </span>
          </div>
        </div>

        <Button
          variant="link"
          onClick={() => setIsLoginMode(!isLoginMode)}
          disabled={isLoading}
          className="p-0 h-auto text-sm text-foreground"
        >
          {isLoginMode
            ? "Don't have an account? Request Access"
            : 'Already have an account? Login'}
        </Button>
      </div>
    </div>
  );
}

export default AuthForm;